import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import UserPermissionsManager from "../components/UserPermissionsManager";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Shield,
  Plus,
  Loader2,
  Edit,
  Trash2,
  Key,
  Check,
  X,
  ChevronRight,
  LayoutDashboard,
  GitCompare,
  ClipboardList,
  FileStack,
  Users,
  Settings,
  Scale,
  Mail,
  HelpCircle,
  Building2,
  BarChart3,
  FileSpreadsheet,
  Package,
  Send,
  FileText,
  Clock,
  Ticket,
  MessageSquare,
  AlertCircle,
  UserCog,
  FormInput,
  CalendarDays,
  UserPlus,
  ShieldAlert,
  Copy,
  ArrowRight,
  Brain,
  PieChart,
  Smartphone,
  Bell,
  Menu,
  Home,
  Folder,
  Database,
  Globe,
  Lock,
  Search,
  Filter,
  Download,
  Upload,
  Circle,
  Star,
  Heart,
  Bookmark,
  Tag,
  Flag,
  Award,
  Target,
  Lightbulb,
  BookOpen,
  GraduationCap,
  Wallet,
  RefreshCw
} from "lucide-react";

// Map icon names to components - includes ALL available icons
const ICON_MAP = {
  ShieldAlert,
  LayoutDashboard,
  GitCompare,
  ClipboardList,
  FileStack,
  Users,
  Settings,
  Scale,
  Mail,
  HelpCircle,
  Building2,
  BarChart3,
  FileSpreadsheet,
  Package,
  Send,
  FileText,
  Clock,
  Ticket,
  MessageSquare,
  AlertCircle,
  UserCog,
  FormInput,
  CalendarDays,
  UserPlus,
  Shield,
  Key,
  Brain,
  PieChart,
  Smartphone,
  Bell,
  Menu,
  Home,
  Folder,
  Database,
  Globe,
  Lock,
  Search,
  Filter,
  Download,
  Upload,
  Circle,
  Star,
  Heart,
  Bookmark,
  Tag,
  Flag,
  Award,
  Target,
  Lightbulb,
  BookOpen,
  GraduationCap,
  Wallet,
  Edit,
  Trash2,
  Plus,
  Check,
  X,
  ChevronRight
};

// Helper to get icon component from string name
const getIconComponent = (iconName) => {
  return ICON_MAP[iconName] || Shield;
};

export default function PermisosPage() {
  const [roles, setRoles] = useState([]);
  const [menuStructure, setMenuStructure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [cloneSource, setCloneSource] = useState("");
  const [cloneTarget, setCloneTarget] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    menu_permissions: {}
  });
  const { getAuthHeaders, user } = useAuth();

  // Generar permisos por defecto basado en la estructura dinámica
  const generateDefaultPermissions = useCallback((fullAccess = false) => {
    const permissions = {};
    menuStructure.forEach(menu => {
      permissions[menu.id] = { access: fullAccess };
      menu.submenus.forEach(submenu => {
        permissions[submenu.id] = { access: fullAccess };
      });
    });
    return permissions;
  }, [menuStructure]);

  // Fetch menu structure from backend - ALWAYS use backend data, never fallback to static
  const fetchMenuStructure = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/system/menus`, getAuthHeaders());
      const menus = res.data || [];
      if (menus.length === 0) {
        console.warn("No menus returned from backend");
        toast.warning("No se encontraron menús del sistema. Contacte al administrador.");
      }
      setMenuStructure(menus);
    } catch (err) {
      console.error("Error fetching menu structure:", err);
      toast.error("Error al cargar los menús del sistema");
      setMenuStructure([]);
    }
  }, [getAuthHeaders]);

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/roles`, getAuthHeaders());
      const apiRoles = res.data || [];
      
      // Los roles ya vienen combinados del backend con los permisos actualizados
      const rolesWithDefaults = apiRoles.map(role => ({
        ...role,
        menu_permissions: role.menu_permissions || {}
      }));
      
      setRoles(rolesWithDefaults);
    } catch (err) {
      console.error("Error:", err);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Función para sincronizar menús y permisos manualmente
  const handleSyncSystem = useCallback(async () => {
    try {
      setSyncing(true);
      const res = await axios.post(`${API}/system/sync`, {}, getAuthHeaders());
      if (res.data?.success) {
        toast.success("Sincronización completada. Recargando datos...");
        // Recargar menús y roles
        await fetchMenuStructure();
        await fetchRoles();
      } else {
        toast.error(res.data?.error || "Error en sincronización");
      }
    } catch (err) {
      console.error("Error syncing:", err);
      toast.error("Error al sincronizar: " + (err.response?.data?.detail || err.message));
    } finally {
      setSyncing(false);
    }
  }, [getAuthHeaders, fetchMenuStructure, fetchRoles]);

  // Al cargar la página, SIEMPRE sincroniza primero para garantizar consistencia en producción
  useEffect(() => {
    const init = async () => {
      try {
        // Sincronizar silenciosamente al cargar la página (sin mostrar spinner)
        // Esto garantiza que los menús estén actualizados incluso si el startup event falló
        await axios.post(`${API}/system/sync`, {}, getAuthHeaders()).catch(e => {
          console.warn("Auto-sync failed (non-blocking):", e);
        });
      } catch (e) {
        // Ignorar errores de sync - no bloquear la carga
      }
      // Cargar datos normalmente
      await fetchMenuStructure();
      await fetchRoles();
    };
    init();
  }, [fetchMenuStructure, fetchRoles, getAuthHeaders]);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      menu_permissions: generateDefaultPermissions(false)
    });
    setEditingRole(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description,
      menu_permissions: role.menu_permissions || generateDefaultPermissions(false)
    });
    setShowDialog(true);
  };

  const handleMenuPermissionChange = (menuId, value) => {
    setFormData(prev => ({
      ...prev,
      menu_permissions: {
        ...prev.menu_permissions,
        [menuId]: { access: value }
      }
    }));
  };

  const handleParentMenuChange = (menu, value) => {
    // Al cambiar el menú padre, cambiar también todos los submenús
    const updates = { [menu.id]: { access: value } };
    menu.submenus.forEach(sub => {
      updates[sub.id] = { access: value };
    });
    
    setFormData(prev => ({
      ...prev,
      menu_permissions: {
        ...prev.menu_permissions,
        ...updates
      }
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("El nombre del rol es requerido");
      return;
    }

    setSaving(true);
    try {
      if (editingRole) {
        // Actualizar rol (tanto del sistema como personalizado)
        await axios.put(`${API}/roles/${editingRole.id}`, {
          name: formData.name,
          description: formData.description,
          menu_permissions: formData.menu_permissions
        }, getAuthHeaders());
        toast.success("Permisos del rol actualizados");
        fetchRoles();
      } else {
        // Crear nuevo rol
        await axios.post(`${API}/roles`, {
          name: formData.name,
          description: formData.description,
          menu_permissions: formData.menu_permissions
        }, getAuthHeaders());
        toast.success("Rol creado exitosamente");
        fetchRoles();
      }
      setShowDialog(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el rol");
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async (id) => {
    const role = roles.find(r => r.id === id);
    if (role?.is_system) {
      toast.error("No se pueden eliminar los roles del sistema");
      return;
    }
    if (!window.confirm("¿Estás seguro de eliminar este rol?")) return;
    
    try {
      await axios.delete(`${API}/roles/${id}`, getAuthHeaders());
      toast.success("Rol eliminado");
      fetchRoles();
    } catch (err) {
      toast.error("Error al eliminar el rol");
    }
  };

  const openCloneDialog = () => {
    setCloneSource("");
    setCloneTarget("");
    setShowCloneDialog(true);
  };

  const handleClonePermissions = async () => {
    if (!cloneSource || !cloneTarget) {
      toast.error("Selecciona rol origen y destino");
      return;
    }
    if (cloneSource === cloneTarget) {
      toast.error("El rol origen y destino no pueden ser el mismo");
      return;
    }

    setCloning(true);
    try {
      const sourceRole = roles.find(r => r.id === cloneSource);
      const targetRole = roles.find(r => r.id === cloneTarget);

      if (!sourceRole || !targetRole) {
        toast.error("Rol no encontrado");
        return;
      }

      // Clone permissions from source to target
      await axios.put(`${API}/roles/${cloneTarget}`, {
        name: targetRole.name,
        description: targetRole.description,
        menu_permissions: { ...sourceRole.menu_permissions }
      }, getAuthHeaders());

      toast.success(`Permisos de "${sourceRole.name}" copiados a "${targetRole.name}"`);
      setShowCloneDialog(false);
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al clonar permisos");
    } finally {
      setCloning(false);
    }
  };

  const countActivePermissions = (permissions) => {
    if (!permissions) return 0;
    return Object.values(permissions).filter(p => p?.access).length;
  };

  const isAdmin = user?.role?.toLowerCase() === 'administrador' || user?.role?.toLowerCase() === 'admin';
  const [activeTab, setActiveTab] = useState("roles");

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="permisos-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Configuración de Permisos
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Administra los roles y acceso a menús del sistema
            </p>
          </div>
          {activeTab === "roles" && (
            <div className="flex items-center gap-3">
              {isAdmin && (
                <Button 
                  onClick={handleSyncSystem}
                  variant="outline"
                  className="border-amber-500 text-amber-600 hover:bg-amber-50"
                  disabled={syncing}
                  data-testid="sync-system-button"
                  title="Sincronizar menús y permisos desde el código del sistema"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Sincronizando...' : 'Sincronizar'}
                </Button>
              )}
              <Button 
                onClick={openCloneDialog}
                variant="outline"
                className="border-[#0C314C] text-[#0C314C] hover:bg-[#0C314C]/10"
                data-testid="clone-permissions-button"
              >
                <Copy className="w-4 h-4 mr-2" />
                Clonar Permisos
              </Button>
              <Button 
                onClick={openNewDialog}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                data-testid="new-role-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Rol
              </Button>
            </div>
          )}
        </div>

        {/* Main Tabs: Roles vs Per-User Permissions */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="roles" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Roles del Sistema
            </TabsTrigger>
            <TabsTrigger value="usuarios" className="flex items-center gap-2">
              <UserCog className="w-4 h-4" />
              Por Usuario
            </TabsTrigger>
          </TabsList>

          {/* Roles Tab Content */}
          <TabsContent value="roles">
            {/* Roles Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {roles.map((role) => (
                  <Card 
                    key={role.id} 
                className="border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md dark:bg-slate-800 transition-shadow"
                data-testid={`role-card-${role.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#0C314C] flex items-center justify-center">
                        <Shield className="w-5 h-5 text-[#45E69A]" />
                      </div>
                      <div>
                        <CardTitle className="text-base font-semibold text-slate-900 dark:text-white">
                          {role.name}
                        </CardTitle>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {countActivePermissions(role.menu_permissions)} accesos activos
                        </p>
                      </div>
                    </div>
                    {role.is_system && (
                      <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded">
                        Sistema
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                    {role.description}
                  </p>
                  
                  {/* Permission Summary - Show first 4 menus */}
                  <div className="space-y-1 mb-4">
                    {menuStructure.slice(0, 4).map(menu => {
                      // Get icon component dynamically
                      const IconComponent = getIconComponent(menu.icon);
                      return (
                        <div key={menu.id} className="flex items-center justify-between text-xs py-1">
                          <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1">
                            <IconComponent className="w-3 h-3" />
                            {menu.label}
                          </span>
                          <span className={`w-5 h-5 rounded flex items-center justify-center ${
                            role.menu_permissions?.[menu.id]?.access
                              ? 'bg-[#45E69A]/20 text-[#0C314C] dark:text-[#45E69A]'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-400'
                          }`}>
                            {role.menu_permissions?.[menu.id]?.access ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <X className="w-3 h-3" />
                            )}
                          </span>
                        </div>
                      );
                    })}
                    {menuStructure.length > 4 && (
                      <p className="text-xs text-slate-400 text-center pt-1">
                        +{menuStructure.length - 4} más...
                      </p>
                    )}
                  </div>
                  
                  {/* Actions - Always show buttons */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(role)}
                      className="flex-1 border-slate-200 dark:border-slate-600"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      {role.is_system ? "Ver/Editar Accesos" : "Editar"}
                    </Button>
                    {!role.is_system && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteRole(role.id)}
                        className="text-slate-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
          </TabsContent>

          {/* Per-User Permissions Tab Content */}
          <TabsContent value="usuarios">
            <UserPermissionsManager />
          </TabsContent>
        </Tabs>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope'] flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#0C314C]" />
                {editingRole ? (editingRole.is_system ? `Permisos: ${editingRole.name}` : "Editar Rol") : "Nuevo Rol"}
              </DialogTitle>
              <DialogDescription>
                Configura el acceso a los menús y submenús del sistema
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-hidden">
              <Tabs defaultValue="info" className="h-full flex flex-col">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="info" disabled={editingRole?.is_system}>Información</TabsTrigger>
                  <TabsTrigger value="permissions">Acceso a Menús</TabsTrigger>
                </TabsList>
                
                <TabsContent value="info" className="flex-1 overflow-auto py-4">
                  {!editingRole?.is_system && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nombre del rol *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Ej: Supervisor"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="description">Descripción</Label>
                        <Input
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData({...formData, description: e.target.value})}
                          placeholder="Descripción del rol..."
                        />
                      </div>
                    </div>
                  )}
                  {editingRole?.is_system && (
                    <div className="flex items-center justify-center h-32 text-slate-500">
                      <p>Los roles del sistema no pueden modificar su nombre o descripción.</p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="permissions" className="flex-1 overflow-hidden">
                  <ScrollArea className="h-[400px] pr-4">
                    <div className="space-y-4 py-2">
                      {menuStructure.map((menu) => {
                        const MenuIcon = getIconComponent(menu.icon);
                        return (
                          <div key={menu.id} className="border rounded-lg dark:border-slate-700 overflow-hidden">
                            {/* Menu Header */}
                            <div 
                              className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 cursor-pointer"
                              onClick={() => handleParentMenuChange(menu, !formData.menu_permissions[menu.id]?.access)}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded bg-[#0C314C] flex items-center justify-center">
                                  <MenuIcon className="w-4 h-4 text-[#45E69A]" />
                                </div>
                                <span className="font-medium text-slate-900 dark:text-white">{menu.label}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                  {menu.submenus.filter(s => formData.menu_permissions[s.id]?.access).length}/{menu.submenus.length}
                                </span>
                                <Checkbox
                                  checked={formData.menu_permissions[menu.id]?.access || false}
                                  onCheckedChange={(checked) => handleParentMenuChange(menu, checked)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            
                            {/* Submenus */}
                            <div className="divide-y divide-slate-100 dark:divide-slate-700">
                              {menu.submenus.map((submenu) => {
                                const SubmenuIcon = getIconComponent(submenu.icon);
                                return (
                                  <div 
                                    key={submenu.id}
                                    className="flex items-center justify-between p-3 pl-14 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                  >
                                    <div className="flex items-center gap-2">
                                      <ChevronRight className="w-3 h-3 text-slate-400" />
                                      <SubmenuIcon className="w-4 h-4 text-slate-400" />
                                      <span className="text-sm text-slate-700 dark:text-slate-300">{submenu.label}</span>
                                    </div>
                                    <Checkbox
                                      checked={formData.menu_permissions[submenu.id]?.access || false}
                                      onCheckedChange={(checked) => handleMenuPermissionChange(submenu.id, checked)}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </div>
            
            <DialogFooter className="border-t dark:border-slate-700 pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Clone Permissions Dialog */}
        <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
          <DialogContent className="max-w-md dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope'] flex items-center gap-2 dark:text-white">
                <Copy className="w-5 h-5 text-[#0C314C]" />
                Clonar Permisos
              </DialogTitle>
              <DialogDescription className="dark:text-slate-400">
                Copia los permisos de un rol a otro. Los permisos del rol destino serán reemplazados.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-6 space-y-6">
              {/* Source Role */}
              <div className="space-y-2">
                <Label className="dark:text-slate-300">Rol Origen (copiar de)</Label>
                <Select value={cloneSource} onValueChange={setCloneSource}>
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600" data-testid="clone-source-select">
                    <SelectValue placeholder="Selecciona el rol origen" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-[#0C314C]" />
                          <span>{role.name}</span>
                          {role.is_system && (
                            <Badge variant="outline" className="text-xs ml-2">Sistema</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {cloneSource && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {countActivePermissions(roles.find(r => r.id === cloneSource)?.menu_permissions)} accesos activos
                  </p>
                )}
              </div>

              {/* Arrow */}
              <div className="flex justify-center">
                <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-[#0C314C]" />
                </div>
              </div>

              {/* Target Role */}
              <div className="space-y-2">
                <Label className="dark:text-slate-300">Rol Destino (copiar a)</Label>
                <Select value={cloneTarget} onValueChange={setCloneTarget}>
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600" data-testid="clone-target-select">
                    <SelectValue placeholder="Selecciona el rol destino" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.id} value={role.id} disabled={role.id === cloneSource}>
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-[#0C314C]" />
                          <span>{role.name}</span>
                          {role.is_system && (
                            <Badge variant="outline" className="text-xs ml-2">Sistema</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {cloneTarget && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Actualmente: {countActivePermissions(roles.find(r => r.id === cloneTarget)?.menu_permissions)} accesos activos
                  </p>
                )}
              </div>

              {/* Warning */}
              {cloneSource && cloneTarget && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-700 dark:text-amber-400 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Los permisos del rol <strong>{roles.find(r => r.id === cloneTarget)?.name}</strong> serán 
                      reemplazados por los del rol <strong>{roles.find(r => r.id === cloneSource)?.name}</strong>.
                    </span>
                  </p>
                </div>
              )}
            </div>
            
            <DialogFooter className="border-t dark:border-slate-700 pt-4">
              <Button variant="outline" onClick={() => setShowCloneDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleClonePermissions} 
                disabled={cloning || !cloneSource || !cloneTarget}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                data-testid="clone-confirm-button"
              >
                {cloning && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {cloning ? "Clonando..." : "Clonar Permisos"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
